import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { getExperiment, getExperimentData } from '../utils/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const MQ_DEFS = [
  { key:'mq2',   label:'MQ-2',   color:'#f87171' },
  { key:'mq3',   label:'MQ-3',   color:'#fb923c' },
  { key:'mq4',   label:'MQ-4',   color:'#facc15' },
  { key:'mq5',   label:'MQ-5',   color:'#a3e635' },
  { key:'mq6',   label:'MQ-6',   color:'#34d399' },
  { key:'mq7',   label:'MQ-7',   color:'#22d3ee' },
  { key:'mq8',   label:'MQ-8',   color:'#818cf8' },
  { key:'mq9',   label:'MQ-9',   color:'#c084fc' },
  { key:'mq135', label:'MQ-135', color:'#f472b6' },
];
const ENV_DEFS = [
  { key:'ens160_eco2', label:'ENS160 eCO2 (ppm)',  color:'#67e8f9' },
  { key:'ens160_tvoc', label:'ENS160 TVOC (ppb)',  color:'#a5b4fc' },
  { key:'aht21_temp',  label:'AHT21 Temp (°C)',    color:'#fca5a5' },
  { key:'aht21_hum',   label:'AHT21 Humidity (%)', color:'#93c5fd' },
  { key:'sgp30_co2',   label:'SGP30 CO2 (ppm)',    color:'#fde68a' },
  { key:'sgp30_tvoc',  label:'SGP30 TVOC (ppb)',   color:'#d9f99d' },
];

function buildChart(rows, defs, yLabel) {
  const labels = rows.map(r => new Date(r.ts).toLocaleTimeString('th-TH'));
  const datasets = defs.map(d => ({
    label: d.label,
    data:  rows.map(r => r[d.key] ?? null),
    borderColor: d.color, backgroundColor: d.color + '22',
    borderWidth:1.5, pointRadius:0, tension:0.3,
  }));
  return { labels, datasets };
}

const chartOpts = (title) => ({
  responsive:true, animation:false,
  plugins:{
    legend:{ labels:{ color:'#94a3b8', font:{size:11} } },
    title:{ display:true, text:title, color:'#cbd5e1' },
  },
  scales:{
    x:{ ticks:{ color:'#475569', maxTicksLimit:15 }, grid:{ color:'#1e293b' } },
    y:{ ticks:{ color:'#475569' }, grid:{ color:'#1e293b' } },
  },
});

function StatRow({ label, value }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0',
      borderBottom:'1px solid #1e293b', fontSize:14 }}>
      <span style={{ color:'#94a3b8' }}>{label}</span>
      <span style={{ color:'#e2e8f0', fontWeight:600 }}>{value}</span>
    </div>
  );
}

export default function ExperimentDetail() {
  const { id } = useParams();
  const [exp,  setExp]  = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getExperiment(id), getExperimentData(id)])
      .then(([e, d]) => { setExp(e.data); setData(d.data); })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p style={{ color:'#94a3b8' }}>กำลังโหลด...</p>;
  if (!exp)    return <p style={{ color:'#ef4444' }}>ไม่พบการทดลอง</p>;

  const mqData  = buildChart(data, MQ_DEFS,  'MQ Sensors (Voltage V)');
  const envData = buildChart(data, ENV_DEFS, 'Environmental Sensors');

  return (
    <div style={{ maxWidth:1100, margin:'0 auto' }}>
      <Link to="/experiments" style={{ color:'#38bdf8', fontSize:13, textDecoration:'none' }}>
        ← รายการการทดลอง
      </Link>

      <h1 style={{ fontSize:22, fontWeight:700, margin:'12px 0 4px' }}>
        #{exp.id} — {exp.title}
      </h1>
      <p style={{ color:'#64748b', fontSize:13, marginBottom:20 }}>
        จำนวนข้อมูล: {data.length} จุด
      </p>

      {/* Summary */}
      <div style={{ background:'#1e293b', borderRadius:12, padding:20, marginBottom:24 }}>
        <h2 style={{ fontSize:16, fontWeight:600, marginBottom:12 }}>สรุปการทดลอง</h2>
        <StatRow label="สถานะ"               value={exp.status} />
        <StatRow label="เวลาเริ่ม"           value={new Date(exp.started_at).toLocaleString('th-TH')} />
        <StatRow label="เวลาเสร็จ"           value={exp.completed_at ? new Date(exp.completed_at).toLocaleString('th-TH') : '—'} />
        <StatRow label="ระยะเวลาทดลอง"       value={`${exp.duration} วินาที`} />
        <StatRow label="ระยะห่างในการบันทึก" value={`${exp.log_interval} วินาที`} />
      </div>

      {/* MQ Chart */}
      {data.length > 0 ? (
        <>
          <div style={{ background:'#0f172a', borderRadius:12, padding:20, marginBottom:20 }}>
            <Line data={mqData} options={chartOpts('MQ Sensors (Voltage V)')} />
          </div>
          <div style={{ background:'#0f172a', borderRadius:12, padding:20, marginBottom:20 }}>
            <Line data={envData} options={chartOpts('Environmental Sensors')} />
          </div>

          {/* Data table (last 50 rows) */}
          <div style={{ background:'#1e293b', borderRadius:12, padding:20 }}>
            <h2 style={{ fontSize:16, fontWeight:600, marginBottom:12 }}>ข้อมูลดิบ (50 แถวล่าสุด)</h2>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr>
                    {['เวลา','MQ2','MQ3','MQ4','MQ5','MQ6','MQ7','MQ8','MQ9','MQ135',
                      'eCO2','TVOC','AQI','Temp','Hum','SGP CO2','SGP TVOC'].map(h => (
                      <th key={h} style={{ padding:'6px 8px', textAlign:'left',
                        borderBottom:'1px solid #334155', color:'#64748b' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.slice(-50).map((r, i) => (
                    <tr key={i} style={{ borderBottom:'1px solid #1e293b' }}>
                      <td style={{ padding:'5px 8px', color:'#94a3b8' }}>
                        {new Date(r.ts).toLocaleTimeString('th-TH')}
                      </td>
                      {['mq2','mq3','mq4','mq5','mq6','mq7','mq8','mq9','mq135',
                        'ens160_eco2','ens160_tvoc','ens160_aqi',
                        'aht21_temp','aht21_hum','sgp30_co2','sgp30_tvoc'].map(k => (
                        <td key={k} style={{ padding:'5px 8px', color:'#cbd5e1' }}>
                          {r[k] !== null && r[k] !== undefined
                            ? (typeof r[k] === 'number' && !Number.isInteger(r[k]) ? r[k].toFixed(3) : r[k])
                            : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <p style={{ color:'#64748b' }}>ยังไม่มีข้อมูลสำหรับการทดลองนี้</p>
      )}
    </div>
  );
}
